import { useEffect, useState, useRef } from 'react';
import { X, CheckCircle, XCircle, Info, AlertTriangle } from 'lucide-react';
import { useNotificationStore } from '@/stores';
import { Button } from './ui/button';

export function Notifications() {
  const { notifications, removeNotification, addNotification } = useNotificationStore();
  const [visibleToasts, setVisibleToasts] = useState<Set<string>>(new Set());
  const shownToastsRef = useRef<Set<string>>(new Set()); // Track toasts we've already shown
  const initialNotificationsRef = useRef<Set<string>>(new Set()); // Track notifications that existed on mount
  const hasInitializedRef = useRef(false);

  // Mark all existing notifications on first mount (don't show these as toasts)
  useEffect(() => {
    if (!hasInitializedRef.current) {
      notifications.forEach((notification) => {
        initialNotificationsRef.current.add(notification.id);
        shownToastsRef.current.add(notification.id); // Mark as already shown (skip toast)
      });
      hasInitializedRef.current = true;
    }
  }, []);

  // Listen for show-notification events
  useEffect(() => {
    const handleShowNotification = (event: CustomEvent) => {
      const { type, title, message } = event.detail;
      console.log('[Notifications] Show notification event received:', event.detail);
      addNotification({ type, title, message });
    };

    window.addEventListener('show-notification', handleShowNotification as EventListener);

    return () => {
      window.removeEventListener('show-notification', handleShowNotification as EventListener);
    };
  }, [addNotification]);

  // Auto-hide toasts after a delay
  useEffect(() => {
    notifications.forEach((notification) => {
      // Skip notifications that existed on initial mount
      if (initialNotificationsRef.current.has(notification.id)) {
        return;
      }

      // Only show each notification once (don't re-show after hiding)
      if (!shownToastsRef.current.has(notification.id)) {
        // Mark as shown
        shownToastsRef.current.add(notification.id);

        // Show the toast
        setVisibleToasts((prev) => new Set([...prev, notification.id]));

        // Set timer to hide the toast after 15 seconds
        if (notification.duration !== 0) setTimeout(() => {
          setVisibleToasts((prev) => {
            const newSet = new Set(prev);
            newSet.delete(notification.id);
            return newSet;
          });
        }, notification.duration ?? 15000);
      }
    });

    // Clean up shown tracking for notifications that were removed from the store
    const currentNotificationIds = new Set(notifications.map(n => n.id));
    shownToastsRef.current.forEach((id) => {
      if (!currentNotificationIds.has(id)) {
        shownToastsRef.current.delete(id);
      }
    });
  }, [notifications]);

  // Filter to only show visible toasts
  const toastsToShow = notifications.filter((n) => visibleToasts.has(n.id));

  if (toastsToShow.length === 0) return null;

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-600" />;
    }
  };

  const getStyles = (type: string) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-amber-50 border-amber-200';
      case 'info':
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const handleDismiss = (id: string) => {
    // Hide the toast immediately
    setVisibleToasts((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  return (
    <div className="fixed top-20 right-4 z-50 flex flex-col gap-2 max-w-md">
      {toastsToShow.map((notification) => (
        <div
          key={notification.id}
          className={`flex items-start gap-3 p-4 rounded-lg border shadow-lg animate-in slide-in-from-right ${getStyles(
            notification.type
          )}`}
        >
          <div className="flex-shrink-0 mt-0.5">{getIcon(notification.type)}</div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 text-sm">
              {notification.title}
            </div>
            {notification.message && (
              <div className="text-sm text-gray-700 mt-1">
                {notification.message}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 flex-shrink-0"
            onClick={() => handleDismiss(notification.id)}
          >
            <X className="w-4 h-4 text-gray-500" />
          </Button>
        </div>
      ))}
    </div>
  );
}
